/**
 * Admin Translations Manager
 *
 * Features:
 * - Add / edit / delete languages (with preset list + custom ISO)
 * - Browse all translation keys grouped by namespace
 * - Edit translations inline per language
 * - AI translate single key or bulk translate (with overwrite toggle)
 * - Add new translation keys
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus, Trash2, Search, Sparkles, Check, X, Loader2,
  Globe, RefreshCw, AlertCircle, Wand2, BookLock, Download, Upload,
} from "lucide-react";
import { api, formatErr } from "../lib/api";
import { useI18n } from "../lib/i18n";

// ISO-639 preset languages for quick-add
const PRESET_LANGUAGES = [
  { code: "en", name: "English",    native_name: "English",    flag_emoji: "GB" },
  { code: "es", name: "Spanish",    native_name: "Español",    flag_emoji: "ES" },
  { code: "ru", name: "Russian",    native_name: "Русский",    flag_emoji: "RU" },
  { code: "ua", name: "Ukrainian",  native_name: "Українська", flag_emoji: "UA" },
  { code: "fr", name: "French",     native_name: "Français",   flag_emoji: "FR" },
  { code: "de", name: "German",     native_name: "Deutsch",    flag_emoji: "DE" },
  { code: "it", name: "Italian",    native_name: "Italiano",   flag_emoji: "IT" },
  { code: "pt", name: "Portuguese", native_name: "Português",  flag_emoji: "PT" },
  { code: "nl", name: "Dutch",      native_name: "Nederlands", flag_emoji: "NL" },
  { code: "pl", name: "Polish",     native_name: "Polski",     flag_emoji: "PL" },
  { code: "tr", name: "Turkish",    native_name: "Türkçe",     flag_emoji: "TR" },
  { code: "ar", name: "Arabic",     native_name: "العربية",    flag_emoji: "SA" },
  { code: "he", name: "Hebrew",     native_name: "עברית",      flag_emoji: "IL" },
  { code: "ja", name: "Japanese",   native_name: "日本語",      flag_emoji: "JP" },
  { code: "ko", name: "Korean",     native_name: "한국어",      flag_emoji: "KR" },
  { code: "zh", name: "Chinese",    native_name: "中文",        flag_emoji: "CN" },
  { code: "hi", name: "Hindi",      native_name: "हिन्दी",       flag_emoji: "IN" },
  { code: "th", name: "Thai",       native_name: "ไทย",         flag_emoji: "TH" },
  { code: "id", name: "Indonesian", native_name: "Indonesia",  flag_emoji: "ID" },
  { code: "vi", name: "Vietnamese", native_name: "Tiếng Việt", flag_emoji: "VN" },
];


export default function AdminTranslationsPanel() {
  const { t, reload: reloadI18n } = useI18n();
  const [section, setSection] = useState("translations");
  const [snapshotBusy, setSnapshotBusy] = useState(false);
  const [snapshotMsg, setSnapshotMsg] = useState(null);
  const fileRef = useRef(null);

  const downloadSnapshot = async () => {
    setSnapshotBusy(true); setSnapshotMsg(null);
    try {
      const { data } = await api.get("/admin/i18n/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "seed_translations.json";
      a.click();
      URL.revokeObjectURL(url);
      setSnapshotMsg(`Exported ${data.stats.translations} translations across ${data.stats.languages} languages. Save the file to /app/backend/seed_translations.json and commit — production will auto-import on next deploy.`);
    } catch (e) {
      setSnapshotMsg("Export failed: " + (e.response?.data?.detail || e.message));
    } finally { setSnapshotBusy(false); }
  };

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSnapshotBusy(true); setSnapshotMsg(null);
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const { data } = await api.post("/admin/i18n/import", payload);
      const r = data.imported;
      setSnapshotMsg(`Imported · +${r.languages_added} langs · ${r.translations_upserted} translations · ${r.glossary_upserted} glossary terms.`);
      reloadI18n();
    } catch (e) {
      setSnapshotMsg("Import failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSnapshotBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid="admin-translations-panel">
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <button
          onClick={() => setSection("translations")}
          className={`tab-pill ${section === "translations" ? "active" : ""}`}
          data-testid="admin-section-translations"
        >
          <Globe size={12}/> {t("admin_translations.tab_translations", "Translations")}
        </button>
        <button
          onClick={() => setSection("glossary")}
          className={`tab-pill ${section === "glossary" ? "active" : ""}`}
          data-testid="admin-section-glossary"
        >
          <BookLock size={12}/> {t("admin_translations.tab_glossary", "Glossary")}
        </button>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <button onClick={downloadSnapshot} disabled={snapshotBusy}
                  className="tab-pill" data-testid="btn-export-translations" title="Download seed_translations.json">
            <Download size={12}/> Export snapshot
          </button>
          <button onClick={() => fileRef.current?.click()} disabled={snapshotBusy}
                  className="tab-pill" data-testid="btn-import-translations" title="Restore from a snapshot file">
            <Upload size={12}/> Import
          </button>
          <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={importFile}/>
        </div>
      </div>

      {snapshotMsg && (
        <div className="luxe-card p-3 text-xs text-teal border-teal/40 mb-4" data-testid="snapshot-msg">{snapshotMsg}</div>
      )}

      {section === "translations" && <TranslationsSection reloadI18n={reloadI18n} t={t}/>}
      {section === "glossary" && <GlossarySection t={t}/>}
    </div>
  );
}


function TranslationsSection({ reloadI18n, t }) {
  const [languages, setLanguages] = useState([]);
  const [sourceLang, setSourceLang] = useState("en");
  const [totalKeys, setTotalKeys] = useState(0);
  const [namespaces, setNamespaces] = useState([]);
  const [activeLang, setActiveLang] = useState("es");
  const [activeNs, setActiveNs] = useState("");
  const [rows, setRows] = useState([]);
  const [missingOnly, setMissingOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [showAddLang, setShowAddLang] = useState(false);
  const [showAddKey, setShowAddKey] = useState(false);

  const showToast = (msg, kind = "ok") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 2500);
  };

  const loadLanguages = async () => {
    try {
      const { data } = await api.get("/admin/i18n/languages");
      setLanguages(data.items || []);
      setSourceLang(data.source_lang || "en");
      setTotalKeys(data.total_keys || 0);
      if (!activeLang && data.items?.length) {
        const firstNonSource = data.items.find((l) => l.code !== (data.source_lang || "en"));
        if (firstNonSource) setActiveLang(firstNonSource.code);
      }
    } catch (e) { setError(formatErr(e)); }
  };

  const loadNamespaces = async () => {
    try {
      const { data } = await api.get("/admin/i18n/namespaces");
      setNamespaces(data.items || []);
    } catch (e) { setError(formatErr(e)); }
  };

  const loadKeys = async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      if (activeNs) params.append("namespace", activeNs);
      if (search) params.append("q", search);
      if (activeLang) params.append("lang", activeLang);
      if (missingOnly) params.append("missing_only", "true");
      const { data } = await api.get(`/admin/i18n/keys?${params.toString()}`);
      setRows(data.items || []);
    } catch (e) { setError(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadLanguages(); loadNamespaces(); }, []);
  useEffect(() => {
    const tmo = setTimeout(() => { if (activeLang) loadKeys(); }, 200);
    return () => clearTimeout(tmo);
  }, [activeLang, activeNs, missingOnly, search]);

  // ---- actions ----------------------------------------------------------
  const saveTranslation = async (row, value, langOverride = null) => {
    const targetLang = langOverride || activeLang;
    const busyKey = `${targetLang}:${row.namespace}.${row.key}`;
    setBusy((b) => ({ ...b, [busyKey]: true }));
    try {
      const { data } = await api.post("/admin/i18n/translations", {
        namespace: row.namespace, key: row.key, lang: targetLang,
        value, auto_translated: false,
      });
      const wiped = data?.auto_translations_invalidated || 0;
      showToast(
        targetLang === sourceLang && wiped > 0
          ? `Saved · ${wiped} auto-translation${wiped === 1 ? "" : "s"} cleared for re-run`
          : "Saved",
      );
      await loadKeys();
      reloadI18n();
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy((b) => ({ ...b, [busyKey]: false })); }
  };

  const aiTranslateOne = async (row) => {
    const busyKey = `ai-${row.namespace}.${row.key}`;
    setBusy((b) => ({ ...b, [busyKey]: true }));
    try {
      await api.post("/admin/i18n/translate", {
        namespace: row.namespace, key: row.key, target_lang: activeLang,
      });
      showToast("Translated");
      await loadKeys();
      reloadI18n();
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy((b) => ({ ...b, [busyKey]: false })); }
  };

  const bulkTranslate = async (overwrite) => {
    if (!window.confirm(
      overwrite
        ? `Re-translate ALL keys${activeNs ? " in namespace '" + activeNs + "'" : ""} to ${activeLang} (overwrites existing)?`
        : `AI-translate all MISSING keys${activeNs ? " in namespace '" + activeNs + "'" : ""} to ${activeLang}?`
    )) return;
    setBulkBusy(true); setError(null);
    try {
      const { data } = await api.post("/admin/i18n/translate-bulk", {
        target_lang: activeLang,
        namespaces: activeNs ? [activeNs] : null,
        overwrite,
      });
      if (data.status === "completed") {
        showToast(`Nothing to translate`);
        setBulkBusy(false);
        return;
      }
      showToast(`Job started — translating ${data.total} keys…`);
      // Poll for job completion
      const jobId = data.job_id;
      const poll = async () => {
        try {
          const { data: st } = await api.get(`/admin/i18n/jobs/${jobId}`);
          const job = st.job;
          if (job.status === "completed") {
            showToast(`Translated ${job.done}/${job.total} keys${job.errors?.length ? ` · ${job.errors.length} errors` : ""}`);
            await loadKeys();
            await loadLanguages();
            reloadI18n();
            setBulkBusy(false);
            return;
          }
          // Update in-progress toast
          setToast({ msg: `Translating ${job.done}/${job.total}…`, kind: "ok" });
          setTimeout(poll, 2500);
        } catch (e) {
          setError(formatErr(e));
          setBulkBusy(false);
        }
      };
      setTimeout(poll, 2000);
    } catch (e) { setError(formatErr(e)); setBulkBusy(false); }
  };

  const deleteKey = async (row) => {
    if (!window.confirm(`Delete key '${row.namespace}.${row.key}' from ALL languages?`)) return;
    try {
      await api.delete(`/admin/i18n/translations/${encodeURIComponent(row.namespace)}/${encodeURIComponent(row.key)}`);
      showToast("Deleted");
      await loadKeys();
      await loadLanguages();
      await loadNamespaces();
      reloadI18n();
    } catch (e) { setError(formatErr(e)); }
  };

  const activeLangMeta = languages.find((l) => l.code === activeLang);

  // ---- render -----------------------------------------------------------
  return (
    <div>
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 rounded-xl px-4 py-2 text-sm shadow-ink ${toast.kind === "ok" ? "bg-teal/20 border border-teal/40 text-teal" : "bg-rose-500/20 border border-rose-400/40 text-rose-200"}`}>
          {toast.msg}
        </div>
      )}

      {/* Language tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {languages.map((l) => (
          <button
            key={l.code}
            onClick={() => setActiveLang(l.code)}
            className={`tab-pill ${activeLang === l.code ? "active" : ""}`}
            data-testid={`trans-lang-${l.code}`}
            title={l.code === sourceLang ? "English source" : `${l.coverage?.translated || 0}/${l.coverage?.total || 0}`}
          >
            <span className="uppercase">{l.code}</span>
            {l.code === sourceLang ? (
              <span className="text-[10px] opacity-70 ml-1.5 text-teal">source</span>
            ) : (
              <span className="text-[10px] opacity-70 ml-1.5">{l.coverage?.percent ?? 0}%</span>
            )}
            {!l.is_active && l.code !== sourceLang && <span className="ml-1 text-[10px] text-muted">(inactive)</span>}
          </button>
        ))}
        <button
          onClick={() => setShowAddLang(true)}
          className="tab-pill !bg-transparent !border !border-dashed !border-white/20 hover:!border-teal"
          data-testid="btn-add-language"
        >
          <Plus size={12}/> Add language
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4 justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted"/>
            <input
              type="text"
              placeholder="Search key or value…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-bare !py-1.5 !pl-9 !pr-3 !text-[13px] w-64"
              data-testid="trans-search"
            />
          </div>
          <select
            value={activeNs}
            onChange={(e) => setActiveNs(e.target.value)}
            className="input-bare !py-1.5 !text-[13px]"
            data-testid="trans-namespace"
          >
            <option value="">All namespaces ({totalKeys})</option>
            {namespaces.map((ns) => (
              <option key={ns.namespace} value={ns.namespace}>{ns.namespace} ({ns.count})</option>
            ))}
          </select>
          <label className="inline-flex items-center gap-2 text-xs text-muted cursor-pointer select-none">
            <input type="checkbox" checked={missingOnly} onChange={(e) => setMissingOnly(e.target.checked)} data-testid="trans-missing-only"/>
            Missing only
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setShowAddKey(true)}
            className="btn btn-outline !text-[12px] !py-1.5 !px-3"
            data-testid="btn-add-key"
          >
            <Plus size={12}/> Add key
          </button>
          <button
            onClick={() => bulkTranslate(false)}
            disabled={bulkBusy}
            className="btn btn-outline !text-[12px] !py-1.5 !px-3"
            data-testid="btn-ai-translate-missing"
          >
            {bulkBusy ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
            AI translate missing
          </button>
          <button
            onClick={() => bulkTranslate(true)}
            disabled={bulkBusy}
            className="btn btn-primary !text-[12px] !py-1.5 !px-3"
            data-testid="btn-ai-retranslate"
          >
            {bulkBusy ? <Loader2 size={12} className="animate-spin"/> : <Wand2 size={12}/>}
            AI retranslate all
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-400/30 bg-rose-500/10 p-3 text-rose-200 text-xs mb-4 flex items-center gap-2">
          <AlertCircle size={14}/> {error}
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl border hairline overflow-hidden">
        <div className="grid grid-cols-[minmax(160px,1fr)_minmax(220px,2fr)_minmax(260px,2.5fr)_auto] gap-3 px-4 py-3 text-[10px] uppercase tracking-widest2 text-muted bg-white/[0.03]">
          <div>Namespace · Key</div>
          <div>English (source) {activeLang === sourceLang && <span className="text-teal ml-1">· editing</span>}</div>
          <div>
            {activeLang === sourceLang
              ? "—"
              : <>{activeLangMeta?.name || activeLang} {activeLangMeta?.flag_emoji ? `(${activeLangMeta.flag_emoji})` : ""}</>}
          </div>
          <div className="text-right">Actions</div>
        </div>
        {loading ? (
          <div className="px-4 py-12 text-center text-muted text-sm">
            <Loader2 size={18} className="animate-spin inline mr-2"/> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-12 text-center text-muted text-sm">
            No keys match your filter.
          </div>
        ) : (
          rows.map((row) => (
            <TranslationRow
              key={`${row.namespace}.${row.key}`}
              row={row}
              lang={activeLang}
              sourceLang={sourceLang}
              onSave={saveTranslation}
              onAITranslate={aiTranslateOne}
              onDelete={deleteKey}
              busy={busy}
            />
          ))
        )}
      </div>

      {showAddLang && (
        <AddLanguageModal
          existing={languages.map((l) => l.code)}
          onClose={() => setShowAddLang(false)}
          onCreated={async () => {
            setShowAddLang(false);
            await loadLanguages();
            reloadI18n();
          }}
        />
      )}
      {showAddKey && (
        <AddKeyModal
          namespaces={namespaces.map((n) => n.namespace)}
          onClose={() => setShowAddKey(false)}
          onCreated={async () => {
            setShowAddKey(false);
            await loadKeys();
            await loadNamespaces();
            await loadLanguages();
            reloadI18n();
          }}
        />
      )}

      <div className="mt-6 flex items-center justify-between text-[11px] text-muted">
        <div>
          {languages.length} languages · {totalKeys} source keys · {namespaces.length} namespaces
        </div>
        <div className="flex items-center gap-3">
          <LanguageManageList
            languages={languages}
            sourceLang={sourceLang}
            onChange={async () => { await loadLanguages(); reloadI18n(); }}
          />
        </div>
      </div>
    </div>
  );
}

// ----- Row --------------------------------------------------------------
function TranslationRow({ row, lang, sourceLang, onSave, onAITranslate, onDelete, busy }) {
  const existing = row.translations?.[lang];
  const [value, setValue] = useState(existing?.value || "");
  const [edited, setEdited] = useState(false);
  const [enValue, setEnValue] = useState(row.en || "");
  const [enEdited, setEnEdited] = useState(false);
  const isAuto = !!existing?.auto_translated;
  const isSourceTab = lang === sourceLang;

  useEffect(() => {
    setValue(existing?.value || "");
    setEdited(false);
  }, [existing?.value, row.namespace, row.key, lang]);

  useEffect(() => {
    setEnValue(row.en || "");
    setEnEdited(false);
  }, [row.en, row.namespace, row.key]);

  const saveKey = `${lang}:${row.namespace}.${row.key}`;
  const enSaveKey = `${sourceLang}:${row.namespace}.${row.key}`;
  const aiKey = `ai-${row.namespace}.${row.key}`;
  const enRows = Math.min(4, Math.max(1, Math.ceil((enValue || "").length / 60)));
  const enChanged = enEdited && (enValue || "") !== (row.en || "");

  return (
    <div className="grid grid-cols-[minmax(160px,1fr)_minmax(220px,2fr)_minmax(260px,2.5fr)_auto] gap-3 px-4 py-3 border-t hairline items-start text-sm"
         data-testid={`trans-row-${row.namespace}-${row.key}`}>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest2 text-muted">{row.namespace}</div>
        <div className="font-mono text-[12px] text-bone truncate">{row.key}</div>
      </div>
      {/* English (source) — now editable */}
      <div>
        <textarea
          value={enValue}
          onChange={(e) => { setEnValue(e.target.value); setEnEdited(true); }}
          rows={enRows}
          className="w-full bg-white/[0.02] border hairline rounded-xl p-2 text-[13px] text-muted leading-snug resize-y focus:text-bone focus:bg-white/[0.04] transition-colors"
          data-testid="trans-en-input"
          placeholder="English source value"
        />
        <div className="flex items-center gap-2 mt-1 text-[10px] text-muted">
          <span className="chip !py-0.5 !text-[10px]">EN · source</span>
          {enChanged && (
            <button
              onClick={() => onSave(row, enValue, sourceLang)}
              disabled={!!busy[enSaveKey]}
              className="btn btn-primary !text-[11px] !py-0.5 !px-2"
              data-testid="trans-en-save"
              title="Save English source — will clear stale auto-translations for re-run"
            >
              {busy[enSaveKey] ? <Loader2 size={11} className="animate-spin"/> : <Check size={11}/>}
              Save EN
            </button>
          )}
          {enChanged && (
            <button
              onClick={() => { setEnValue(row.en || ""); setEnEdited(false); }}
              className="text-muted/70 hover:text-bone text-[11px] underline-offset-2 hover:underline"
              data-testid="trans-en-cancel"
            >
              cancel
            </button>
          )}
        </div>
      </div>
      {/* Active language */}
      <div>
        {isSourceTab ? (
          <div className="text-[11px] text-muted/70 italic px-2 py-2">
            Editing English in the column on the left.
          </div>
        ) : (
          <>
            <textarea
              value={value}
              onChange={(e) => { setValue(e.target.value); setEdited(true); }}
              rows={Math.min(4, Math.max(1, Math.ceil((value || "").length / 60)))}
              className={`w-full bg-white/[0.03] border hairline rounded-xl p-2 text-[13px] text-bone leading-snug resize-y ${!existing ? "border-rose-400/40" : ""}`}
              placeholder={existing ? "" : "Missing — click AI ✨ or type"}
              data-testid="trans-input"
            />
            <div className="flex items-center gap-2 mt-1 text-[10px] text-muted">
              {!existing && <span className="text-rose-300">Missing</span>}
              {existing && isAuto && <span className="chip !py-0.5 !text-[10px]"><Sparkles size={9}/> AI</span>}
              {existing && !isAuto && <span className="chip success !py-0.5 !text-[10px]"><Check size={9}/> Manual</span>}
            </div>
          </>
        )}
      </div>
      <div className="flex items-center gap-1">
        {edited && !isSourceTab && (
          <button
            onClick={() => onSave(row, value)}
            disabled={!!busy[saveKey]}
            className="btn btn-primary !text-[11px] !py-1 !px-2"
            data-testid="trans-save"
            title="Save"
          >
            {busy[saveKey] ? <Loader2 size={11} className="animate-spin"/> : <Check size={11}/>}
          </button>
        )}
        {!isSourceTab && (
          <button
            onClick={() => onAITranslate(row)}
            disabled={!!busy[aiKey]}
            className="btn btn-outline !text-[11px] !py-1 !px-2"
            data-testid="trans-ai"
            title="AI translate this key"
          >
            {busy[aiKey] ? <Loader2 size={11} className="animate-spin"/> : <Sparkles size={11}/>}
          </button>
        )}
        <button
          onClick={() => onDelete(row)}
          className="btn btn-outline !text-[11px] !py-1 !px-2 !border-rose-400/30 !text-rose-300 hover:!bg-rose-500/10"
          data-testid="trans-delete"
          title="Delete key from all languages"
        >
          <Trash2 size={11}/>
        </button>
      </div>
    </div>
  );
}

// ----- Add language modal ------------------------------------------------
function AddLanguageModal({ existing, onClose, onCreated }) {
  const [mode, setMode] = useState("preset");
  const [preset, setPreset] = useState("");
  const [custom, setCustom] = useState({ code: "", name: "", native_name: "", flag_emoji: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const presetOptions = PRESET_LANGUAGES.filter((p) => !existing.includes(p.code));

  const save = async (payload) => {
    setBusy(true); setError(null);
    try {
      await api.post("/admin/i18n/languages", { ...payload, is_active: true, sort_order: 99 });
      onCreated();
    } catch (e) { setError(formatErr(e)); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink/80 p-4" data-testid="add-lang-modal">
      <div className="glass-solid rounded-3xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="eyebrow">Add language</div>
            <h3 className="section-title text-2xl mt-1">New language</h3>
          </div>
          <button onClick={onClose} className="text-muted hover:text-bone"><X size={18}/></button>
        </div>
        <div className="flex gap-2 mb-4">
          <button onClick={() => setMode("preset")} className={`tab-pill ${mode === "preset" ? "active" : ""}`}>Preset</button>
          <button onClick={() => setMode("custom")} className={`tab-pill ${mode === "custom" ? "active" : ""}`}>Custom ISO</button>
        </div>
        {mode === "preset" ? (
          <>
            <select
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
              className="input-bare w-full mb-4"
              data-testid="preset-select"
            >
              <option value="">— Choose a language —</option>
              {presetOptions.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.flag_emoji} {p.code.toUpperCase()} · {p.native_name}
                </option>
              ))}
            </select>
            <button
              disabled={!preset || busy}
              onClick={() => {
                const p = PRESET_LANGUAGES.find((x) => x.code === preset);
                save(p);
              }}
              className="btn btn-primary w-full"
              data-testid="preset-add"
            >
              {busy ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>}
              Add language
            </button>
          </>
        ) : (
          <div className="space-y-3">
            <input
              placeholder="ISO code (e.g. jp, nl, pt)"
              className="input-bare w-full"
              value={custom.code}
              onChange={(e) => setCustom({ ...custom, code: e.target.value.toLowerCase() })}
              data-testid="custom-code"
            />
            <input placeholder="Name in English (e.g. Dutch)" className="input-bare w-full"
                   value={custom.name} onChange={(e) => setCustom({ ...custom, name: e.target.value })}
                   data-testid="custom-name"/>
            <input placeholder="Native name (e.g. Nederlands)" className="input-bare w-full"
                   value={custom.native_name} onChange={(e) => setCustom({ ...custom, native_name: e.target.value })}
                   data-testid="custom-native"/>
            <input placeholder="Flag emoji or ISO country code (e.g. NL)" className="input-bare w-full"
                   value={custom.flag_emoji} onChange={(e) => setCustom({ ...custom, flag_emoji: e.target.value })}
                   data-testid="custom-flag"/>
            <button
              disabled={!custom.code || !custom.name || !custom.native_name || busy}
              onClick={() => save(custom)}
              className="btn btn-primary w-full"
              data-testid="custom-add"
            >
              {busy ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>}
              Add language
            </button>
          </div>
        )}
        {error && <div className="text-rose-300 text-xs mt-3">{error}</div>}
        <div className="text-muted text-[11px] mt-4 flex items-start gap-2">
          <Sparkles size={12} className="shrink-0 mt-0.5"/>
          Tip: after adding the language, click "AI translate all missing" to populate it automatically.
        </div>
      </div>
    </div>
  );
}

// ----- Add key modal ----------------------------------------------------
function AddKeyModal({ namespaces, onClose, onCreated }) {
  const [form, setForm] = useState({ namespace: namespaces[0] || "common", key: "", en_value: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await api.post("/admin/i18n/keys", form);
      onCreated();
    } catch (e) { setError(formatErr(e)); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink/80 p-4" data-testid="add-key-modal">
      <div className="glass-solid rounded-3xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="eyebrow">Add key</div>
            <h3 className="section-title text-2xl mt-1">New translation key</h3>
          </div>
          <button onClick={onClose} className="text-muted hover:text-bone"><X size={18}/></button>
        </div>
        <div className="space-y-3">
          <select value={form.namespace} onChange={(e) => setForm({ ...form, namespace: e.target.value })} className="input-bare w-full">
            {namespaces.map((ns) => <option key={ns} value={ns}>{ns}</option>)}
            <option value="_new_">+ New namespace…</option>
          </select>
          {form.namespace === "_new_" && (
            <input placeholder="New namespace (e.g. checkout)" className="input-bare w-full"
                   onChange={(e) => setForm({ ...form, namespace: e.target.value })}/>
          )}
          <input placeholder="key (snake_case, e.g. submit_btn)" className="input-bare w-full"
                 value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })}
                 data-testid="add-key-key"/>
          <textarea placeholder="English value" className="input-bare w-full"
                    rows={3}
                    value={form.en_value} onChange={(e) => setForm({ ...form, en_value: e.target.value })}
                    data-testid="add-key-value"/>
          <button
            disabled={!form.namespace || !form.key || !form.en_value || busy}
            onClick={save}
            className="btn btn-primary w-full"
            data-testid="add-key-submit"
          >
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>}
            Create key
          </button>
          {error && <div className="text-rose-300 text-xs">{error}</div>}
        </div>
      </div>
    </div>
  );
}

// ----- Glossary section (do-not-translate brand terms) -----------------
function GlossarySection({ t }) {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ term: "", note: "" });

  const reload = async () => {
    try {
      const { data } = await api.get("/admin/i18n/glossary");
      setItems(data.items || []);
    } catch (e) { setError(formatErr(e)); }
  };
  useEffect(() => { reload(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.term.trim()) return;
    setBusy(true); setError(null);
    try {
      await api.post("/admin/i18n/glossary", form);
      setForm({ term: "", note: "" });
      await reload();
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy(false); }
  };

  const remove = async (term) => {
    if (!window.confirm(`Remove '${term}' from glossary?`)) return;
    try {
      await api.delete(`/admin/i18n/glossary/${encodeURIComponent(term)}`);
      await reload();
    } catch (e) { setError(formatErr(e)); }
  };

  return (
    <div data-testid="glossary-section">
      <div className="mb-6">
        <h3 className="section-title text-3xl mb-1">{t("admin_translations.glossary_title", "Glossary")}</h3>
        <p className="text-sm text-muted max-w-2xl">{t("admin_translations.glossary_subtitle", "Terms AI will NEVER translate — brand names, destinations, currencies, venue names. Kept verbatim across every language.")}</p>
      </div>

      <form onSubmit={add} className="flex flex-wrap items-center gap-2 mb-6" data-testid="glossary-add-form">
        <input
          type="text"
          value={form.term}
          onChange={(e) => setForm({ ...form, term: e.target.value })}
          placeholder={t("admin_translations.glossary_term_placeholder", "e.g. Meet & Mint")}
          className="input-bare !py-2 !text-sm w-56"
          data-testid="glossary-term-input"
        />
        <input
          type="text"
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          placeholder={t("admin_translations.glossary_note_placeholder", "Why? (optional)")}
          className="input-bare !py-2 !text-sm flex-1 min-w-[220px]"
          data-testid="glossary-note-input"
        />
        <button
          type="submit"
          disabled={busy || !form.term.trim()}
          className="btn btn-primary !text-[12px] !py-2"
          data-testid="glossary-add-submit"
        >
          {busy ? <Loader2 size={12} className="animate-spin"/> : <Plus size={12}/>}
          {t("admin_translations.glossary_add", "Add term")}
        </button>
      </form>

      {error && (
        <div className="rounded-xl border border-rose-400/30 bg-rose-500/10 p-3 text-rose-200 text-xs mb-4 flex items-center gap-2">
          <AlertCircle size={14}/> {error}
        </div>
      )}

      {items.length === 0 ? (
        <div className="luxe-card p-10 text-center text-muted text-sm">
          {t("admin_translations.glossary_empty", "No terms yet. Add brand names, place names, or tickers.")}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="glossary-list">
          {items.map((g) => {
            const slug = g.term.replace(/\s+/g, '-').toLowerCase();
            return (
              <div
                key={g.term}
                className="group inline-flex items-center gap-2 rounded-2xl border hairline bg-white/[0.03] px-3 py-2 text-[13px] hover:border-teal/40 transition"
                data-testid={`glossary-chip-${slug}`}
                data-glossary-term={g.term}
                title={g.note || ""}
              >
                <BookLock size={12} className="text-teal shrink-0"/>
                <div className="min-w-0">
                  <div className="text-bone leading-tight" data-testid={`glossary-chip-label-${slug}`}>{g.term}</div>
                  {g.note && <div className="text-[10px] text-muted truncate max-w-[180px]">{g.note}</div>}
                </div>
                <button
                  onClick={() => remove(g.term)}
                  className="opacity-0 group-hover:opacity-100 transition text-rose-300/80 hover:text-rose-300 ml-1"
                  title="Remove"
                  data-testid={`glossary-chip-remove-${slug}`}
                >
                  <X size={12}/>
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-8 text-[11px] text-muted max-w-2xl">
        <Sparkles size={11} className="inline mr-1"/> Tip: glossary terms are injected into every AI translation prompt, so the model preserves them verbatim in all languages.
      </div>
    </div>
  );
}

function LanguageManageList({ languages, sourceLang, onChange }) {
  const [open, setOpen] = useState(false);
  const toggleActive = async (l) => {
    try {
      await api.patch(`/admin/i18n/languages/${l.code}`, { is_active: !l.is_active });
      onChange();
    } catch {/**/}
  };
  const remove = async (l) => {
    if (!window.confirm(`Delete language '${l.code}' and all its translations?`)) return;
    try {
      await api.delete(`/admin/i18n/languages/${l.code}`);
      onChange();
    } catch {/**/}
  };
  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1.5 hover:text-teal" data-testid="manage-langs">
        <Globe size={12}/> Manage languages
      </button>
      {open && (
        <div className="absolute right-0 bottom-full mb-2 w-72 glass-solid rounded-2xl p-3 shadow-ink z-50">
          {languages.map((l) => (
            <div key={l.code} className="flex items-center justify-between gap-2 py-1.5 text-xs">
              <div className="flex-1 min-w-0">
                <div className="text-bone truncate">{l.name}
                  {l.code === sourceLang && <span className="ml-2 text-[10px] text-teal">source</span>}
                </div>
                <div className="text-muted text-[10px]">{l.code.toUpperCase()} · {l.coverage?.percent || 0}%</div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => toggleActive(l)}
                  className={`text-[10px] px-2 py-1 rounded ${l.is_active ? "bg-teal/10 text-teal" : "bg-white/5 text-muted"}`}
                  disabled={l.code === sourceLang}
                  title={l.is_active ? "Disable (hide from users)" : "Enable"}
                >
                  {l.is_active ? "Active" : "Hidden"}
                </button>
                {l.code !== sourceLang && (
                  <button onClick={() => remove(l)} className="text-rose-300/80 hover:text-rose-300" title="Delete">
                    <Trash2 size={12}/>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
